import { useEffect, useMemo, useState } from "react";
import { useIsPresent } from "framer-motion";

import { CommonLocation, LocationGroup } from "./CommonLocation";

import { Location as LocationType } from "@otta/search/schema";
import { useUserPreferences } from "@otta/search/providers/UserPreferencesProvider/useUserPreferences";
import {
  BASE_CA_LOCATIONS,
  BASE_EU_LOCATIONS,
  BASE_US_LOCATIONS,
  Country,
  locationCountries,
} from "@otta/search/constants/locations";
import { useUserLocation } from "@otta/search/hooks/useUserLocation";

const isSupportedCountry: (c: Country | undefined) => boolean = country =>
  country !== undefined;

const getDefaultRemoteLocation = (
  country: Country | undefined
): LocationType => {
  switch (country) {
    case Country.US:
      return LocationType.RemoteUs;
    case Country.UK:
      return LocationType.RemoteUk;
    case Country.CA:
      return LocationType.RemoteCanada;
    case Country.FR:
    case Country.DE:
    case Country.NL:
    case Country.ES:
    case Country.IE:
      return LocationType.RemoteEurope;
    default:
      return LocationType.RemoteUk;
  }
};

export function LocationSelector({
  onPreferenceSet,
  showRemote = true,
}: {
  onPreferenceSet: (isPreferenceSet: boolean) => void;
  showRemote?: boolean;
}): React.ReactElement {
  const {
    preferencesData,
    updatePreferences,
    setOnlyRemoteOpportunities,
    onlyRemoteOpportunities,
  } = useUserPreferences();

  // Dynamic options array based on feature flag
  const options: readonly LocationGroup[] = useMemo(
    () => [
      {
        visible: isSupportedCountry,
        locations: BASE_EU_LOCATIONS,
      },
      {
        visible: isSupportedCountry,
        locations: BASE_US_LOCATIONS,
      },
      {
        visible: isSupportedCountry,
        locations: BASE_CA_LOCATIONS,
      },
      {
        visible: (c: Country | undefined) => !isSupportedCountry(c),
        locations: [
          ...BASE_EU_LOCATIONS,
          ...BASE_US_LOCATIONS,
          ...BASE_CA_LOCATIONS,
        ].sort(),
      },
    ],
    []
  );

  const { country } = useUserLocation();

  const locationPreferences = useMemo(
    () => preferencesData?.locationPreferences ?? [],
    [preferencesData?.locationPreferences]
  );

  const baseLocationPreferences = useMemo(
    () => locationPreferences.filter(x => !x.location.includes("REMOTE")),
    [locationPreferences]
  );

  const [remoteSelected, setRemoteSelected] = useState(false);

  useEffect(() => {
    if (preferencesData?.locationPreferences) {
      const hasRemotePrefs = preferencesData.locationPreferences.some(val =>
        val.location.includes("REMOTE")
      );
      const hasAnyPrefs = preferencesData.locationPreferences.length > 0;

      const shouldSelectRemote =
        hasRemotePrefs || (onlyRemoteOpportunities && hasAnyPrefs);

      setRemoteSelected(shouldSelectRemote);
    }
  }, [
    preferencesData?.locationPreferences,
    onlyRemoteOpportunities,
    updatePreferences,
  ]);

  const isPresent = useIsPresent();
  const isValid = remoteSelected || locationPreferences.length > 0;

  useEffect(() => {
    if (isPresent) {
      onPreferenceSet(isValid);
    }
  }, [isValid, isPresent, onPreferenceSet]);

  const handleAddingRemoteOpportunities = (
    locations: ReadonlyArray<LocationType>
  ): ReadonlyArray<LocationType> => {
    return Array.from(
      new Set(
        locations.flatMap(l => {
          switch (locationCountries[l]) {
            case Country.US:
              return [l, LocationType.RemoteUs];
            case Country.UK:
              return [l, LocationType.RemoteUk];
            case Country.NL:
              return [l, LocationType.RemoteNetherlands];
            case Country.DE:
              return [l, LocationType.RemoteGermany];
            case Country.ES:
              return [l, LocationType.RemoteSpain];
            case Country.IE:
              return [l, LocationType.RemoteIreland];
            case Country.FR:
              return [l, LocationType.RemoteFrance];
            case Country.CA:
              return [l, LocationType.RemoteCanada];
            default:
              return [l];
          }
        })
      )
    );
  };

  const updateNewLocationPreferences = (
    locations: ReadonlyArray<LocationType>,
    remote: boolean
  ) => {
    const basePreferences = remote
      ? handleAddingRemoteOpportunities(locations)
      : locations;

    // If remote is checked but no locations result, keep a generic remote preference to
    // avoid the remote checkbox being uncheck automatically
    const newLocationPreferences =
      remote && basePreferences.length === 0
        ? [getDefaultRemoteLocation(country)]
        : basePreferences;

    updatePreferences({
      locationPreferences: newLocationPreferences.map(location => ({
        location,
      })),
    });

    setOnlyRemoteOpportunities(!newLocationPreferences.length && remote);
  };

  const handleChange = (locations: readonly LocationType[]) => {
    updateNewLocationPreferences(locations, remoteSelected);
  };

  const handleRemoteChange = (checked: boolean) => {
    setRemoteSelected(checked);
    const set = baseLocationPreferences.map(pref => pref.location);

    if (checked && set.length === 0) {
      // If Remote is checked but no physical locations, add a generic remote preference
      updatePreferences({
        locationPreferences: [{ location: getDefaultRemoteLocation(country) }],
      });
      setOnlyRemoteOpportunities(true);
    } else {
      updateNewLocationPreferences(set, checked);
    }
  };

  return (
    <CommonLocation
      options={options}
      valueChanged={handleChange}
      value={baseLocationPreferences.map(l => l.location)}
      remoteChanged={handleRemoteChange}
      remoteSelected={remoteSelected}
      loading={!preferencesData}
      showRemote={showRemote}
    />
  );
}
